import React from "react";

/* ═══════════════════════════════════════════════════════════════
   Math & Data — 8 templates
   coordinate_plane, geometric_shapes, number_line, bar_chart,
   line_graph, fraction_visual, angle_protractor, probability_tree
   ═══════════════════════════════════════════════════════════════ */

/* ─── 1. Coordinate Plane ─────────────────────────────────── */
export const CoordinatePlane: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Coordinate Plane";
  const accentColor = (props.accentColor as string) || "#3498db";
  const flowProgress = (props.flowProgress as number) || 0;
  const points = (props.points as number[][]) || [[1,2],[3,5],[5,3],[7,6]];
  const xLabel = (props.xLabel as string) || "x";
  const yLabel = (props.yLabel as string) || "y";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Grid */}
      {Array.from({length:9},(_,i)=>(
        <g key={i}>
          <line x1={120+i*65} y1="60" x2={120+i*65} y2="480" stroke="#1e3a5f" strokeWidth="0.8" />
          <line x1="120" y1={60+i*52.5} x2="640" y2={60+i*52.5} stroke="#1e3a5f" strokeWidth="0.8" />
        </g>
      ))}
      {/* Axes */}
      <line x1="120" y1="480" x2="660" y2="480" stroke="#ecf0f1" strokeWidth="2" />
      <line x1="120" y1="60" x2="120" y2="500" stroke="#ecf0f1" strokeWidth="2" />
      {/* Axis labels */}
      {Array.from({length:9},(_,i)=>(
        <text key={i} x={120+i*65} y="500" textAnchor="middle" fill="#7f8c8d" fontSize="11">{i}</text>
      ))}
      <text x="670" y="485" fill="#7f8c8d" fontSize="12">{xLabel}</text>
      <text x="110" y="55" fill="#7f8c8d" fontSize="12">{yLabel}</text>
      {/* Plot points */}
      {points.map((p,i)=>{
        const px=120+p[0]*65, py=480-p[1]*52.5;
        const visible = flowProgress >= i/(points.length-1);
        return (
          <g key={i} opacity={visible?1:0.2}>
            <circle cx={px} cy={py} r="6" fill={accentColor} />
            {/* Line to next */}
            {i<points.length-1 && visible && flowProgress>=(i+1)/(points.length-1) && (
              <line x1={px} y1={py} x2={120+points[i+1][0]*65} y2={480-points[i+1][1]*52.5}
                stroke={accentColor} strokeWidth="2" />
            )}
            <text x={px} y={py-12} textAnchor="middle" fill="#ecf0f1" fontSize="10">({p[0]},{p[1]})</text>
          </g>
        );
      })}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 2. Geometric Shapes ─────────────────────────────────── */
export const GeometricShapes: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Geometric Shapes";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const rotation = (props.rotation as number) || 0;
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <defs>
        <filter id="gs-sh"><feDropShadow dx="2" dy="3" stdDeviation="4" floodOpacity="0.3" /></filter>
      </defs>
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Triangle */}
      <g filter="url(#gs-sh)" transform={`rotate(${rotation*0.1},200,200)`}>
        <polygon points="200,100 280,280 120,280"
          fill={activeIndex===0?"#e74c3c":"none"} stroke="#e74c3c" strokeWidth="3" opacity={activeIndex===0?0.8:0.5} />
        <text x="200" y="320" textAnchor="middle" fill="#ecf0f1" fontSize="14">Triangle</text>
      </g>
      {/* Square */}
      <g filter="url(#gs-sh)" transform={`rotate(${rotation*0.15},400,200)`}>
        <rect x="330" y="120" width="140" height="140" rx="4"
          fill={activeIndex===1?"#3498db":"none"} stroke="#3498db" strokeWidth="3" opacity={activeIndex===1?0.8:0.5} />
        <text x="400" y="300" textAnchor="middle" fill="#ecf0f1" fontSize="14">Square</text>
      </g>
      {/* Circle */}
      <g filter="url(#gs-sh)">
        <circle cx="600" cy="190" r="80"
          fill={activeIndex===2?"#2ecc71":"none"} stroke="#2ecc71" strokeWidth="3" opacity={activeIndex===2?0.8:0.5} />
        <text x="600" y="300" textAnchor="middle" fill="#ecf0f1" fontSize="14">Circle</text>
      </g>
      {/* Pentagon */}
      <g filter="url(#gs-sh)" transform={`rotate(${rotation*0.08},250,430)`}>
        <polygon points="250,360 310,390 290,455 210,455 190,390"
          fill={activeIndex===3?"#f39c12":"none"} stroke="#f39c12" strokeWidth="3" opacity={activeIndex===3?0.8:0.5} />
        <text x="250" y="490" textAnchor="middle" fill="#ecf0f1" fontSize="14">Pentagon</text>
      </g>
      {/* Hexagon */}
      <g filter="url(#gs-sh)" transform={`rotate(${rotation*0.12},550,430)`}>
        <polygon points="550,360 600,380 600,430 550,450 500,430 500,380"
          fill={activeIndex===4?"#9b59b6":"none"} stroke="#9b59b6" strokeWidth="3" opacity={activeIndex===4?0.8:0.5} />
        <text x="550" y="490" textAnchor="middle" fill="#ecf0f1" fontSize="14">Hexagon</text>
      </g>
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 3. Number Line ──────────────────────────────────────── */
export const NumberLine: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Number Line";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const highlightPoints = (props.highlightPoints as number[]) || [2, 5, 8];
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Line */}
      <line x1="60" y1="300" x2="740" y2="300" stroke="#ecf0f1" strokeWidth="3" />
      {/* Arrow heads */}
      <polygon points="740,300 725,292 725,308" fill="#ecf0f1" />
      <polygon points="60,300 75,292 75,308" fill="#ecf0f1" />
      {/* Ticks */}
      {Array.from({length:11},(_,i)=>{
        const x=80+i*62;
        const isHighlight = highlightPoints.includes(i);
        return (
          <g key={i}>
            <line x1={x} y1="285" x2={x} y2="315" stroke={isHighlight?"#e74c3c":"#ecf0f1"} strokeWidth={isHighlight?3:1.5} />
            <text x={x} y="340" textAnchor="middle" fill={isHighlight?"#e74c3c":"#bdc3c7"} fontSize="14" fontWeight={isHighlight?"700":"400"}>{i}</text>
            {isHighlight && <circle cx={x} cy="300" r="8" fill="#e74c3c" opacity="0.8" />}
          </g>
        );
      })}
      {/* Bracket if activeIndex shows range */}
      {activeIndex >= 0 && highlightPoints.length >= 2 && (
        <g>
          <line x1={80+highlightPoints[0]*62} y1="260" x2={80+highlightPoints[highlightPoints.length-1]*62} y2="260"
            stroke="#3498db" strokeWidth="2" />
          <text x={(80+highlightPoints[0]*62+80+highlightPoints[highlightPoints.length-1]*62)/2} y="250"
            textAnchor="middle" fill="#3498db" fontSize="13">range</text>
        </g>
      )}
      <text x="400" y="440" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 4. Bar Chart ────────────────────────────────────────── */
export const BarChart: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Bar Chart";
  const values = (props.values as number[]) || [40, 70, 55, 90, 65];
  const labels2 = (props.barLabels as string[]) || ["A", "B", "C", "D", "E"];
  const fillPercent = (props.fillPercent as number) ?? 100;
  const accentColor = (props.accentColor as string) || "#3498db";
  const xLabel = (props.xLabel as string) || "Categories";
  const yLabel = (props.yLabel as string) || "Value";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Axes */}
      <line x1="120" y1="80" x2="120" y2="460" stroke="#ecf0f1" strokeWidth="2" />
      <line x1="120" y1="460" x2="700" y2="460" stroke="#ecf0f1" strokeWidth="2" />
      {/* Y ticks */}
      {[0, 25, 50, 75, 100].map((v) => {
        const y = 460 - (v / 100) * 350;
        return (
          <g key={v}>
            <line x1="114" y1={y} x2="120" y2={y} stroke="#7f8c8d" strokeWidth="1" />
            <text x="104" y={y + 4} textAnchor="end" fill="#7f8c8d" fontSize="10">{v}</text>
          </g>
        );
      })}
      {/* Bars */}
      {values.map((v, i) => {
        const bw = 80, gap = 30;
        const x = 160 + i * (bw + gap);
        const maxH = 350;
        const h = (v / 100) * maxH * (fillPercent / 100);
        return (
          <g key={i}>
            <rect x={x} y={460 - h} width={bw} height={h} rx="4"
              fill={accentColor} opacity="0.8" />
            <text x={x + bw / 2} y="490" textAnchor="middle" fill="#bdc3c7" fontSize="14">{labels2[i]}</text>
            <text x={x + bw / 2} y={455 - h} textAnchor="middle" fill="#ecf0f1" fontSize="12">{v}%</text>
          </g>
        );
      })}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
      <text x="400" y="520" textAnchor="middle" fill="#94a3b8" fontSize="12">{xLabel}</text>
      <text x="40" y="260" textAnchor="middle" fill="#94a3b8" fontSize="12" transform="rotate(-90,40,260)">{yLabel}</text>
    </svg>
  );
};

/* ─── 5. Line Graph ───────────────────────────────────────── */
export const LineGraph: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Line Graph";
  const values = (props.values as number[]) || [10, 40, 30, 70, 55, 80];
  const flowProgress = (props.flowProgress as number) ?? 1;
  const accentColor = (props.accentColor as string) || "#2ecc71";
  const xLabels = (props.xLabels as string[]) || values.map((_, i) => `P${i + 1}`);
  const xLabel = (props.xLabel as string) || "Samples";
  const yLabel = (props.yLabel as string) || "Value";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      <line x1="100" y1="80" x2="100" y2="460" stroke="#ecf0f1" strokeWidth="2" />
      <line x1="100" y1="460" x2="720" y2="460" stroke="#ecf0f1" strokeWidth="2" />
      {/* Grid */}
      {[160,260,360,460].map(y=>(<line key={y} x1="100" y1={y} x2="720" y2={y} stroke="#1e3a5f" strokeWidth="0.5" />))}
      {/* Y ticks */}
      {[0, 25, 50, 75, 100].map((v) => {
        const y = 460 - (v / 100) * 380;
        return (
          <g key={v}>
            <line x1="94" y1={y} x2="100" y2={y} stroke="#7f8c8d" strokeWidth="1" />
            <text x="86" y={y + 4} textAnchor="end" fill="#7f8c8d" fontSize="10">{v}</text>
          </g>
        );
      })}
      {/* Data points & line */}
      {values.map((v, i) => {
        const x = 140 + i * 100;
        const y = 460 - (v / 100) * 380;
        const show = flowProgress >= i / (values.length - 1);
        return (
          <g key={i} opacity={show ? 1 : 0.15}>
            <circle cx={x} cy={y} r="5" fill={accentColor} />
            {i > 0 && show && flowProgress >= (i - 1) / (values.length - 1) && (
              <line x1={140 + (i - 1) * 100} y1={460 - (values[i - 1] / 100) * 380}
                x2={x} y2={y} stroke={accentColor} strokeWidth="2.5" />
            )}
          </g>
        );
      })}
      {/* X labels */}
      {xLabels.map((lx, i) => (
        <text key={lx} x={140 + i * 100} y="485" textAnchor="middle" fill="#94a3b8" fontSize="10">{lx}</text>
      ))}
      <text x="400" y="520" textAnchor="middle" fill="#94a3b8" fontSize="12">{xLabel}</text>
      <text x="40" y="260" textAnchor="middle" fill="#94a3b8" fontSize="12" transform="rotate(-90,40,260)">{yLabel}</text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 6. Fraction Visual ──────────────────────────────────── */
export const FractionVisual: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Fractions";
  const numerator = (props.numerator as number) || 3;
  const denominator = (props.denominator as number) || 8;
  const accentColor = (props.accentColor as string) || "#e74c3c";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Pie representation */}
      {Array.from({ length: denominator }, (_, i) => {
        const startA = (i / denominator) * 360 - 90;
        const endA = ((i + 1) / denominator) * 360 - 90;
        const sR = startA * Math.PI / 180, eR = endA * Math.PI / 180;
        const large = endA - startA > 180 ? 1 : 0;
        const d = `M400,250 L${400 + Math.cos(sR) * 150},${250 + Math.sin(sR) * 150} A150,150 0 ${large} 1 ${400 + Math.cos(eR) * 150},${250 + Math.sin(eR) * 150}Z`;
        return (
          <path key={i} d={d} fill={i < numerator ? accentColor : "#2c3e50"}
            stroke="#0d1117" strokeWidth="2" opacity={i < numerator ? 0.8 : 0.3} />
        );
      })}
      {/* Fraction text */}
      <text x="400" y="460" textAnchor="middle" fill="#ecf0f1" fontSize="36" fontWeight="700">
        {numerator}/{denominator}
      </text>
      <text x="400" y="540" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 7. Angle Protractor ─────────────────────────────────── */
export const AngleProtractor: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Protractor";
  const value = (props.value as number) || 45;
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Semi-circle protractor */}
      <path d="M160,400 A240,240 0 0 1 640,400" fill="#1c2833" stroke="#546e7a" strokeWidth="2" />
      {/* Degree marks */}
      {Array.from({ length: 37 }, (_, i) => {
        const deg = i * 5;
        const rad = (180 - deg) * Math.PI / 180;
        const inner = deg % 10 === 0 ? 210 : 225;
        return (
          <g key={i}>
            <line x1={400 + Math.cos(rad) * inner} y1={400 - Math.sin(rad) * inner}
              x2={400 + Math.cos(rad) * 240} y2={400 - Math.sin(rad) * 240}
              stroke="#7f8c8d" strokeWidth={deg % 30 === 0 ? 2 : 0.8} />
            {deg % 30 === 0 && (
              <text x={400 + Math.cos(rad) * 195} y={400 - Math.sin(rad) * 195 + 4}
                textAnchor="middle" fill="#bdc3c7" fontSize="12">{deg}°</text>
            )}
          </g>
        );
      })}
      {/* Measured angle */}
      {(() => {
        const rad = (180 - value) * Math.PI / 180;
        return (
          <>
            <line x1="400" y1="400" x2={400 + Math.cos(Math.PI) * 200} y2={400 - Math.sin(Math.PI) * 200}
              stroke="#ecf0f1" strokeWidth="2" />
            <line x1="400" y1="400" x2={400 + Math.cos(rad) * 200} y2={400 - Math.sin(rad) * 200}
              stroke={accentColor} strokeWidth="3" />
            {/* Arc */}
            <path d={`M${400 + Math.cos(Math.PI) * 60},400 A60,60 0 ${value > 180 ? 1 : 0} 1 ${400 + Math.cos(rad) * 60},${400 - Math.sin(rad) * 60}`}
              fill="none" stroke={accentColor} strokeWidth="2" />
            <text x={400 + Math.cos((Math.PI + rad) / 2) * 80} y={400 - Math.sin((Math.PI + rad) / 2) * 80}
              textAnchor="middle" fill={accentColor} fontSize="16" fontWeight="600">{value}°</text>
          </>
        );
      })()}
      <text x="400" y="520" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};

/* ─── 8. Probability Tree ─────────────────────────────────── */
export const ProbabilityTree: React.FC<Record<string, unknown>> = (props) => {
  const label = (props.label as string) || "Probability Tree";
  const activeIndex = (props.activeIndex as number) ?? -1;
  const accentColor = (props.accentColor as string) || "#3498db";
  return (
    <svg viewBox="0 0 800 600" width="100%" height="100%">
      <rect width="800" height="600" fill="#0d1117" rx="16" />
      {/* Level 0 - root */}
      <circle cx="150" cy="280" r="20" fill={activeIndex === 0 ? accentColor : "#34495e"} stroke="#546e7a" strokeWidth="2" />
      <text x="150" y="285" textAnchor="middle" fill="#ecf0f1" fontSize="11">Start</text>
      {/* Level 1 */}
      {[{ y: 160, lbl: "A (0.6)" }, { y: 400, lbl: "B (0.4)" }].map((n, i) => (
        <g key={i}>
          <line x1="170" y1="280" x2="330" y2={n.y} stroke={activeIndex >= 1 ? accentColor : "#546e7a"} strokeWidth="1.5" />
          <circle cx="350" cy={n.y} r="20" fill={activeIndex === 1 ? accentColor : "#34495e"} stroke="#546e7a" strokeWidth="2" />
          <text x="350" y={n.y + 4} textAnchor="middle" fill="#ecf0f1" fontSize="10">{n.lbl}</text>
        </g>
      ))}
      {/* Level 2 */}
      {[
        { x: 550, y: 100, from: 160, lbl: "A∩C (0.36)" },
        { x: 550, y: 220, from: 160, lbl: "A∩D (0.24)" },
        { x: 550, y: 340, from: 400, lbl: "B∩C (0.24)" },
        { x: 550, y: 460, from: 400, lbl: "B∩D (0.16)" },
      ].map((n, i) => (
        <g key={i}>
          <line x1="370" y1={n.from} x2={n.x - 20} y2={n.y} stroke={activeIndex >= 2 ? accentColor : "#546e7a"} strokeWidth="1.5" />
          <circle cx={n.x} cy={n.y} r="20" fill={activeIndex === 2 ? accentColor : "#34495e"} stroke="#546e7a" strokeWidth="2" />
          <text x={n.x + 30} y={n.y + 4} fill="#bdc3c7" fontSize="10">{n.lbl}</text>
        </g>
      ))}
      <text x="400" y="555" textAnchor="middle" fill="#ecf0f1" fontSize="20" fontWeight="600">{label}</text>
    </svg>
  );
};
